import React, { useEffect, useMemo } from 'react';
import { arrayOf, bool, func, number, oneOfType, shape, string } from 'prop-types';
import { Box, Button, Stack, Typography } from '@mui/material';
import * as R from 'ramda';
import { NoAvailableCard } from 'common';
import { useUserRolePermissions } from 'hooks';
import { useGetProductsQuery } from '../../../customerAuthorizationsEndpoints';
import ProductsGrid from './ProductsGrid';
import { getProductId, getUniqueProducts } from './productRules';

const AddProductsSection = ({
  customerIdName,
  existingProducts,
  isOpen,
  onCancel,
  onAddProducts,
  selectedProductIds,
  setSelectedProductIds,
  setIsOpen,
  terminals,
}) => {
  const { currentAffiliate = {} } = useUserRolePermissions();
  const { customerIdName: companyCustomerIdName, dataCompanyId } = currentAffiliate;

  useEffect(() => {
    setSelectedProductIds([]);
  }, [customerIdName, existingProducts, setSelectedProductIds]);

  const params = {
    customerIdName,
    supplier: { companyCustomerIdName, dataCompanyId },
    terminals: R.map(R.prop('terminalId'), terminals),
  };
  const { data: productsQuery = [], isFetching } = useGetProductsQuery(
    { params },
    { skip: R.or(R.not(isOpen), R.isEmpty(terminals)) }
  );

  const uniqueProducts = useMemo(() => getUniqueProducts(productsQuery), [productsQuery]);
  const existingProductIds = useMemo(
    () => existingProducts.map(product => String(getProductId(product))),
    [existingProducts]
  );
  const candidates = useMemo(
    () =>
      uniqueProducts.filter(product => !existingProductIds.includes(String(getProductId(product)))),
    [existingProductIds, uniqueProducts]
  );

  const handleCancel = () => {
    setSelectedProductIds([]);
    onCancel();
    setIsOpen(false);
  };

  const handleAdd = () => {
    const selectedProducts = candidates.filter(product =>
      selectedProductIds.includes(getProductId(product))
    );
    onAddProducts(selectedProducts);
    setSelectedProductIds([]);
    setIsOpen(false);
  };

  const selectionGridProps = {
    checkboxSelection: true,
    disableRowSelectionOnClick: false,
    loading: isFetching,
    onRowSelectionModelChange: model => setSelectedProductIds(model),
    rowSelectionModel: selectedProductIds,
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'common.white',
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Stack spacing={2} sx={{ p: 1.25 }}>
        {!isFetching && R.isEmpty(candidates) ? (
          <NoAvailableCard
            suggestions="All available products are already added to this authorization."
            type="products"
          />
        ) : (
          <ProductsGrid
            darkHeader
            framed={false}
            gridHeight={280}
            gridProps={selectionGridProps}
            panelTitle="Select Products to Add"
            products={candidates}
            quickFilterAriaLabel="Filter products to add"
            quickFilterPlaceholder="Filter products to add..."
            quickFilterPlacement="header"
            showSelectionHeaderCheckbox
          />
        )}
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
          <Typography color="text.secondary" variant="body2">
            {R.isEmpty(selectedProductIds)
              ? 'Select products above'
              : `${selectedProductIds.length} selected`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleCancel} variant="outlined">
              Cancel
            </Button>
            <Button
              disabled={R.isEmpty(selectedProductIds)}
              onClick={handleAdd}
              variant="contained"
            >
              {`Add${R.isEmpty(selectedProductIds) ? '' : ` (${selectedProductIds.length})`}`}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

AddProductsSection.propTypes = {
  customerIdName: oneOfType([number, string]),
  existingProducts: arrayOf(
    shape({
      productId: oneOfType([number, string]),
    })
  ),
  isOpen: bool,
  onAddProducts: func.isRequired,
  onCancel: func.isRequired,
  selectedProductIds: arrayOf(oneOfType([number, string])),
  setIsOpen: func.isRequired,
  setSelectedProductIds: func.isRequired,
  terminals: arrayOf(
    shape({
      terminalId: oneOfType([number, string]),
    })
  ),
};

AddProductsSection.defaultProps = {
  customerIdName: null,
  existingProducts: [],
  isOpen: false,
  selectedProductIds: [],
  terminals: [],
};

export default AddProductsSection;
